import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Button, Spinner, ProgressBar, Alert, Card, Table, Badge } from "react-bootstrap";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { CheckCircleFill, XCircleFill } from 'react-bootstrap-icons';

const MockTestPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { testId, userDetails } = location.state || {};

  // LOCAL REGISTRATION ID (Only for non-login users)
  const localRegistrationId = localStorage.getItem("mockRegistrationId");

  const [testData, setTestData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testSubmitted, setTestSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1800);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHint, setShowHint] = useState({});
  const [selectedOptionStatus, setSelectedOptionStatus] = useState({});
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showDetailedResults, setShowDetailedResults] = useState(false);

  const { bearerAuth } = useApi();
  const { getToken, user } = useAuth();

  useEffect(() => {
    if (!testId) {
      setError("Invalid session. Please register again.");
      navigate("/", { replace: true });
      return;
    }

    const loadTestData = async () => {
      try {
        const token = getToken();

        const testResponse = await bearerAuth(
          `mock-tests/by-id`,
          "POST",
          { test_id: testId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (testResponse.data?.status) {
          setTestData(testResponse.data.data || testResponse.data.data[0]);

          const questionsResponse = await bearerAuth(
            `mock-tests/questions/list`,
            "POST",
            { test_id: testId },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (questionsResponse.data?.status) {
            const loadedQuestions = questionsResponse.data.data || [];
            
            // Add full URLs for images and videos
            const questionsWithMediaUrls = loadedQuestions.map(question => {
              const updatedQuestion = { ...question };
              
              // Add image URL if image exists
              const imagePath = question.image_url || question.image || null;
              if (imagePath) {
                updatedQuestion.fullImageUrl = getMediaUrl(imagePath);
              }
              
              // Add video URL if video exists
              const videoPath = question.video_url || question.video || null;
              if (videoPath) {
                updatedQuestion.fullVideoUrl = getMediaUrl(videoPath);
              }
              
              return updatedQuestion;
            });
            
            setQuestions(questionsWithMediaUrls);
            
            // Initialize hint visibility state
            const hintState = {};
            questionsWithMediaUrls.forEach(q => {
              hintState[q.key] = false;
            });
            setShowHint(hintState);
            
          } else setError("Could not load test questions.");
        } else setError("Could not load test details.");
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError("Failed to load test. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadTestData();
  }, [testId]);

  // Timer
  useEffect(() => {
    let interval;
    if (timeLeft > 0 && !testSubmitted && questions.length > 0) {
      interval = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    } else if (timeLeft === 0 && !testSubmitted) {
      submitTest();
    }
    return () => clearInterval(interval);
  }, [timeLeft, testSubmitted, questions.length]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const handleAnswerSelect = (key, option) => {
    // Don't allow changing answer if already selected
    if (answers[key]) return;

    // Find the question
    const question = questions.find(q => q.key === key);
    if (!question) return;

    // Check if answer is correct
    const isCorrect = question.correct_option === option;
    
    // Update answers
    setAnswers((prev) => ({ ...prev, [key]: option }));
    
    // Update selected option status
    setSelectedOptionStatus((prev) => ({
      ...prev,
      [key]: {
        selected: option,
        isCorrect: isCorrect,
        correctOption: question.correct_option,
        explanation: question.explanation
      }
    }));

    // Show hint if available (automatically when answer selected)
    if (question.hint) {
      setShowHint((prev) => ({ ...prev, [key]: true }));
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const toggleHint = (key) => {
    setShowHint((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const submitTest = async () => {
    try {
      const token = getToken();

      let payload = {
        test_id: testId,
        answers: answers,
      };

      if (user?.id) {
        payload = {
          ...payload,
          name: user.username,
          email: user.email,
          phone: user.contact,
        };
      } else {
        payload = {
          ...payload,
          id: localRegistrationId,
        };
      }

      const response = await bearerAuth(
        "mock-free-submit",
        "POST",
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const apiMessage = response?.data?.message || "Something went wrong";

      if (response?.data?.status) {
        setSubmissionResult(response.data.data);
        setTestSubmitted(true);

        if (!user?.id) {
          localStorage.removeItem("mockRegistrationId");
        }

        await Swal.fire({
          title: "Success",
          text: apiMessage,
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        await Swal.fire({
          title: "Notice",
          text: apiMessage,
          icon: "warning",
          confirmButtonText: "OK",
        });
      }

    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit test.";

      await Swal.fire({
        title: "Notice",
        text: apiMessage,
        icon: "warning",
        confirmButtonText: "OK",
      });
    }
  };

  const getMediaUrl = (mediaPath) => {
    if (!mediaPath) return null;

    const normalizePath = (value) =>
      String(value)
        .replace(/\\/g, "/")
        .replace(/\/public\/public\//g, "/public/")
        .replace(/\/backend\/public\/uploads\//g, "/backend/uploads/")
        .replace(/\/public\/uploads\//g, "/uploads/")
        .replace(/\/public\/storage\//g, "/storage/")
        .replace(/^backend\/public\/uploads\//, "backend/uploads/")
        .replace(/^public\/public\//, "public/")
        .replace(/^public\/uploads\//, "uploads/")
        .replace(/^public\/storage\//, "storage/")
        .replace(/^\/+/, "");

    if (/^https?:\/\//i.test(mediaPath)) {
      return normalizePath(mediaPath);
    }

    const clean = normalizePath(mediaPath);
    const base = (process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    if (!base) return `/${clean}`;
    if (clean.startsWith("uploads/")) return `${base}/${clean}`;
    if (clean.startsWith("storage/")) return `${base}/${clean}`;
    if (clean.startsWith("backend/uploads/")) return `${base}/${clean}`;
    if (clean.startsWith("public/uploads/")) return `${base}/${clean}`;
    if (clean.startsWith("public/storage/")) return `${base}/${clean}`;
    return `${base}/${clean}`;
  };

  const renderQuestionResult = (question, index) => {
    const userAnswer = answers[question.key] || 'Not answered';
    const correctAnswer = submissionResult?.correctAnswers[question.key] || question.correct_option;
    const isCorrect = userAnswer === correctAnswer;

    return (
      <div key={question.key} className="mb-4 p-3 border rounded">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Question {index + 1}</h5>
          <span className={`badge ${isCorrect ? 'bg-success' : 'bg-danger'}`}>
            {isCorrect ? 'Correct' : 'Incorrect'}
          </span>
        </div>

        <p className="fw-bold mb-3">{question.question}</p>

        {/* Display image in results */}
        {question.fullImageUrl && (
          <div className="mb-3 text-center">
            <img
              src={question.fullImageUrl}
              alt="Question illustration"
              className="img-fluid rounded"
              style={{ maxHeight: '400px' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Display video link in results */}
        {question.fullVideoUrl && (
          <div className="mb-3">
            <a
              href={question.fullVideoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-primary"
            >
              <i className="fas fa-play-circle me-1"></i>
              View Video
            </a>
          </div>
        )}

        <div className="row">
          <div className="col-md-6">
            <div className="card mb-3">
              <div className="card-header bg-info text-white">
                Your Answer
              </div>
              <div className="card-body">
                {userAnswer === 'Not answered' ? (
                  <span className="text-muted">Not answered</span>
                ) : (
                  <>
                    <p className="mb-1">
                      <strong>{userAnswer}:</strong> {question[`option_${userAnswer.toLowerCase()}`]}
                    </p>
                    {!isCorrect && (
                      <span className="text-danger">
                        <i className="fas fa-times-circle me-1"></i>
                        Incorrect
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card">
              <div className="card-header bg-success text-white">
                Correct Answer
              </div>
              <div className="card-body">
                <p className="mb-1">
                  <strong>{correctAnswer}:</strong> {question[`option_${correctAnswer.toLowerCase()}`]}
                </p>
                {isCorrect ? (
                  <span className="text-success">
                    <i className="fas fa-check-circle me-1"></i>
                    You got it right!
                  </span>
                ) : (
                  <span className="text-success">
                    <i className="fas fa-check-circle me-1"></i>
                    This was the correct answer
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Show explanation in results */}
        {question.explanation && (
          <div className="mt-3 p-3 bg-light rounded">
            <h6 className="mb-2">
              <i className="fas fa-info-circle me-2 text-primary"></i>
              Explanation:
            </h6>
            <p className="mb-0">{question.explanation}</p>
          </div>
        )}
      </div>
    );
  };

  // Render result page
  if (testSubmitted && submissionResult) {
    const { score, total_questions, percentage } = submissionResult;
    const passed = percentage >= 70;
    const correctCount = score;
    
    return (
      <div className="container py-4">
        <div className="test-results">
          <div className="text-center py-4">
            <h3>Test Results</h3>
            <div className={`display-4 fw-bold ${percentage >= 70 ? 'text-success' : 'text-danger'}`}>
              {percentage}%
            </div>
            <p className="mt-3">
              {percentage >= 70 ? 'Congratulations! You passed!' : 'Sorry, you did not pass. Please try again.'}
            </p>
            <div className="progress mt-3" style={{ height: '20px' }}>
              <div
                className={`progress-bar ${percentage >= 70 ? 'bg-success' : 'bg-danger'}`}
                role="progressbar"
                style={{ width: `${percentage}%` }}
                aria-valuenow={percentage}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {percentage}%
              </div>
            </div>
            <p className="mt-2">
              You answered <strong>{correctCount}</strong> out of <strong>{total_questions}</strong> questions correctly.
            </p>

            <button
              className={`btn ${showDetailedResults ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
              onClick={() => setShowDetailedResults(!showDetailedResults)}
            >
              {showDetailedResults ? 'Hide Detailed Results' : 'View Detailed Results'}
              <i className={`fas ${showDetailedResults ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`}></i>
            </button>

            <button
              className="btn btn-primary ms-3"
              onClick={() => navigate('/')}
            >
              Return Home
            </button>
          </div>

          {showDetailedResults && (
            <div className="detailed-results mt-4">
              <h4 className="mb-4 text-center">Question-by-Question Breakdown</h4>
              {questions.map((question, index) => renderQuestionResult(question, index))}

              <div className="text-center mt-4">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/')}
                >
                  Return Home
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isLoading)
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p>Loading test...</p>
      </div>
    );

  if (error)
    return (
      <div className="container py-5 text-center">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => navigate("/")}>Go Home</Button>
      </div>
    );

  if (questions.length === 0)
    return (
      <div className="text-center py-5">
        <Alert>No questions found.</Alert>
      </div>
    );

  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = answers[currentQuestion?.key || ""];
  const selectedStatus = selectedOptionStatus[currentQuestion?.key];
  const hasMedia = currentQuestion?.fullImageUrl || currentQuestion?.fullVideoUrl;

  // Calculate progress
  const answeredCount = Object.keys(answers).length;
  const progressPercentage = (answeredCount / questions.length) * 100;

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-9">
          <div className="test-header mb-4 p-3 bg-light rounded shadow-sm">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h4 className="mb-1 text-primary">
                  <i className="fas fa-book me-2"></i>
                  {testData?.title || "Mock Test"}
                </h4>
                <p className="mb-0 text-muted">
                  <i className="fas fa-user me-2"></i>
                  {userDetails?.name || user?.username || 'User'}
                </p>
              </div>
              <div className="col-md-6 text-md-end mt-2 mt-md-0">
                {!testSubmitted && questions.length > 0 && (
                  <div className="d-flex align-items-center justify-content-end">
                    <div className="me-3">
                      <span className="badge bg-secondary">
                        Q{currentQuestionIndex + 1}/{questions.length}
                      </span>
                    </div>
                    <div className="me-3">
                      <span className="badge bg-info">
                        Answered: {answeredCount}/{questions.length}
                      </span>
                    </div>
                    <div className="timer-container">
                      <small>Time Left: {formatTime(timeLeft)}</small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <ProgressBar
            now={progressPercentage}
            label={`${answeredCount}/${questions.length} Answered`}
            className="mb-4"
            variant={answeredCount === questions.length ? "success" : "info"}
          />

          <div className="test-content">
            <div className="card shadow-sm">
              <div className="card-body">
                <h4 className="card-title mb-4">{currentQuestion?.question}</h4>
                
                {currentQuestion.hint && (
                  <div className="d-flex justify-content-end mb-3">
                    <Button 
                      variant="outline-info" 
                      size="sm"
                      onClick={() => toggleHint(currentQuestion.key)}
                    >
                      {showHint[currentQuestion.key] ? "Hide Hint" : "Show Hint"}
                    </Button>
                  </div>
                )}
                
                {showHint[currentQuestion?.key] && currentQuestion?.hint && (
                  <div className="alert alert-info py-2 mb-3">
                    <strong>💡 Hint:</strong> {currentQuestion.hint}
                  </div>
                )}

                <div className="row g-4 align-items-start">
                  {/* Media Section - Takes left column if media exists */}
                  {hasMedia && (
                    <div className="col-12 col-lg-5">
                      {currentQuestion.fullImageUrl && (
                        <div className="mb-3 text-center">
                          <img
                            src={currentQuestion.fullImageUrl}
                            alt="Question illustration"
                            className="img-fluid rounded"
                            style={{ width: '100%', maxHeight: '320px', objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      {currentQuestion.fullVideoUrl && (
                        <div className="mb-3 text-center">
                          <video 
                            controls 
                            key={currentQuestion?.key} 
                            className="img-fluid rounded shadow-sm"
                            style={{ width: '100%', maxHeight: '320px', backgroundColor: '#000' }}
                          >
                            <source src={currentQuestion.fullVideoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Options Section - Adjusts width based on media presence */}
                  <div className={hasMedia ? "col-12 col-lg-7" : "col-12"}>
                    {["A", "B", "C", "D"].map((opt) => {
                      const key = `option_${opt.toLowerCase()}`;
                      const text = currentQuestion?.[key];
                      if (!text) return null;

                      const getOptionClassName = () => {
                        if (!selectedStatus || selectedStatus.selected !== opt) {
                          return "p-3 border rounded mb-2 bg-light";
                        }
                        
                        if (selectedStatus.isCorrect) {
                          return "p-3 border rounded mb-2 bg-success text-white";
                        } else {
                          return "p-3 border rounded mb-2 bg-danger text-white";
                        }
                      };

                      return (
                        <div
                          key={opt}
                          onClick={() => handleAnswerSelect(currentQuestion.key, opt)}
                          className={getOptionClassName()}
                          style={{ cursor: selectedAnswer ? "default" : "pointer", transition: "all 0.3s" }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <span>
                              <strong>{opt}:</strong> {text}
                            </span>
                            {selectedStatus?.selected === opt && (
                              <span className={`badge ${selectedStatus.isCorrect ? 'bg-light text-success' : 'bg-light text-danger'}`}>
                                {selectedStatus.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Show explanation after selection */}
                {selectedStatus && selectedStatus.selected && (
                  <div className={`mt-4 p-3 rounded ${selectedStatus.isCorrect ? 'bg-success-subtle' : 'bg-danger-subtle'}`}>
                    <div className="d-flex align-items-start">
                      <i className={`fas fa-${selectedStatus.isCorrect ? 'check-circle' : 'times-circle'} fs-4 me-3 ${selectedStatus.isCorrect ? 'text-success' : 'text-danger'}`}></i>
                      <div>
                        <h6 className={`mb-2 ${selectedStatus.isCorrect ? 'text-success' : 'text-danger'}`}>
                          {selectedStatus.isCorrect ? 'Correct!' : 'Incorrect!'}
                        </h6>
                        {currentQuestion.explanation && (
                          <>
                            <p className="mb-1 fw-bold">Explanation:</p>
                            <p className="mb-0">{currentQuestion.explanation}</p>
                          </>
                        )}
                        {!currentQuestion.explanation && (
                          <p className="mb-0">
                            {selectedStatus.isCorrect 
                              ? 'Great job! You selected the correct answer.' 
                              : `The correct answer is ${selectedStatus.correctOption}.`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="card-footer bg-light">
                <div className="d-flex justify-content-between">
                  <Button
                    variant="secondary"
                    onClick={handlePrevQuestion}
                    disabled={currentQuestionIndex === 0}
                  >
                    Previous
                  </Button>
                  <div>
                    {answers[currentQuestion?.key] && (
                      <span className="badge bg-success me-2">
                        <i className="fas fa-check me-1"></i>
                        Answered
                      </span>
                    )}
                  </div>
                  {currentQuestionIndex < questions.length - 1 ? (
                    <Button variant="primary" onClick={handleNextQuestion}>
                      Next
                    </Button>
                  ) : (
                    <Button 
                      variant="success" 
                      onClick={submitTest}
                    >
                      Submit Test
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTestPage;
